import React, { useMemo, useRef, useState } from "react";
import { useDashboardData, Course } from "@/context/DashboardDataContext";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
const INITIAL_VISIBLE = 6;
export const ExploreCourses: React.FC = () => {
  const {
    categories,
    courses,
    isLoading,
    isError,
    errorMessage
  } = useDashboardData();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("for-you");
  const [showAll, setShowAll] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const handleScrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };
  const handleScrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };
  const filteredCourses = useMemo(() => {
    if (selectedCategoryId === "for-you") {
      return courses;
    }
    return courses.filter((c) => c.categoryId && c.categoryId === selectedCategoryId);
  }, [courses, selectedCategoryId]);
  const visibleCourses: Course[] = useMemo(() => {
    if (showAll) return filteredCourses;
    return filteredCourses.slice(0, INITIAL_VISIBLE);
  }, [filteredCourses, showAll]);
  const hasMore = filteredCourses.length > INITIAL_VISIBLE;
  return <section className="w-full mb-10" data-explore-courses-section="true">
      {/* Header */}
      <div className="mb-6">
        <div className="w-full flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-base lg:text-lg font-semibold text-foreground">Courses</span>
            
          </div>
        </div>

        {/* Category tabs with horizontal scroll */}
        <div className="w-full relative group/close mb-4">
          {/* Scroll-right button */}
          <button type="button" onClick={handleScrollRight} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none hover:text-accent-foreground h-10 w-10 absolute right-1 top-1/2 z-[7] -mt-1 -translate-y-1/2 rounded-full border border-border bg-background/70 shadow-sm backdrop-blur transition-all hover:bg-background/80 opacity-0 group-hover/close:opacity-100" aria-label="Scroll right">
            <ChevronRight className="h-5 w-5" />
          </button>

          {/* Scroll container */}
          <div className="w-full overflow-x-auto scrollbar-hide" ref={scrollContainerRef}>
            <div className="flex gap-2 pb-2 px-0.5 min-w-max">
              {/* For You tab */}
              <button type="button" onClick={() => setSelectedCategoryId("for-you")} className={["inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200", selectedCategoryId === "for-you" ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90" : "bg-card border border-primary/10 text-primary/90 hover:border-primary/20 hover:bg-card hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"].join(" ")}>
                For You
              </button>

              {/* Dynamic category tabs */}
              {categories.map((category) => {
              const isActive = selectedCategoryId === category.id;
              return <button key={category.id} type="button" onClick={() => setSelectedCategoryId(category.id)} className={["inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200", isActive ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90" : "bg-card border border-primary/10 text-primary/90 hover:border-primary/20 hover:bg-card hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"].join(" ")}>
                    <span className="truncate">{category.name}</span>
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? <ExploreCoursesSkeleton /> : isError ? <div className="text-sm text-muted-foreground bg-muted/5 border border-muted/20 rounded-lg px-3 py-2">
          {errorMessage ?? "Something went wrong while loading courses."}
        </div> : filteredCourses.length === 0 ? <div className="text-sm text-muted-foreground">
          No courses found in this category yet.
        </div> : <>
          {/* Course grid */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 py-1">
            {visibleCourses.map((course) => <CourseCard key={course.id} course={course} />)}
          </div>

          {/* Show More / Show Less */}
          {hasMore && <div className="flex justify-center mt-6">
              <button type="button" onClick={() => setShowAll((prev) => !prev)} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none bg-transparent h-9 rounded-lg px-4 gap-2 text-primary hover:text-primary/80">
                <span>{showAll ? "Show Less" : "Show More"}</span>
                <ChevronRight className={`h-4 w-4 transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
              </button>
            </div>}
        </>}
    </section>;
};

// ------------ Course Card ------------

type CourseCardProps = {
  course: Course;
};
const CourseCard: React.FC<CourseCardProps> = ({
  course
}) => {
  const href = course.url ?? `/course/${course.id}`;
  return <a href={href} className="block h-full group">
      <div className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-md hover:border-primary/20">
        {/* Cover */}
        <div className="h-14 relative flex items-center justify-end px-4" style={{
        backgroundImage: course.coverImageUrl || course.coverUrl ? `url("${course.coverImageUrl || course.coverUrl}")` : "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.6) 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat"
      }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

          {/* Icon */}
          <div className="absolute bottom-0 left-4 transform translate-y-1/2 z-10">
            <div className="rounded-full p-1 shadow-sm bg-card">
              {course.iconUrl ?
            <img src={course.iconUrl} alt="" className="h-4 w-4 rounded-full object-cover" /> :
            course.iconEmoji ?
            <span className="h-4 w-4 flex items-center justify-center text-xs">{course.iconEmoji}</span> :

            <div className="h-4 w-4 rounded-full" style={{
              background: course.accentColor ? course.accentColor : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))"
            }} />
            }
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="flex items-center min-h-[44px] px-4 py-4 pt-5">
          <div className="flex flex-1 min-w-0">
            <div className="flex flex-col flex-1 min-w-0 gap-1">
              <span className="text-sm font-medium truncate tracking-wide text-foreground group-hover:text-primary transition-colors">
                {course.title}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                  {course.author || "Shattara AI"}
                </span>
            </div>
          </div>
        </div>
      </div>
    </a>;
};

// ------------ Skeleton ------------

const ExploreCoursesSkeleton: React.FC = () => {
  const skeletonItems = Array.from({
    length: INITIAL_VISIBLE
  });
  return <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 py-1">
      {skeletonItems.map((_, idx) => <div key={idx} className="bg-card border border-border rounded-2xl h-[120px] flex flex-col overflow-hidden">
          <Skeleton className="h-14 rounded-none" />
          <div className="flex-1 px-4 py-3 space-y-2">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>)}
    </div>;
};